import {
  isClientAccessDenyKind,
  pickLocalizedApiMessage,
  type ClientAccessDenyKind,
  type EntitlementDenialItem,
} from 'pi-kiosk-shared';
import {
  isUnsafeAdminApiMessage,
  publicMessageForHttpStatus,
} from '../../shared/errors/adminPublicApiFailureMessage.js';
import i18n from '../../shared/i18n/i18n.js';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Package F — typed wire item for `details.entitlementDenials`.
 * Prefer {@link parseEntitlementDenials} so unknown kinds are dropped.
 */
export type ApiErrorEntitlementDenial = EntitlementDenialItem;

/**
 * Structured diagnostics on the error envelope (ONE SoT).
 * Prefer `err.response.details` — do not invent `ApiError.details`.
 */
export interface ApiErrorDetails {
  missingBlockKeys?: string[];
  /**
   * Explain-only axis denials. Values ⊆ {@link ClientAccessDenyKind}.
   * Preserve existing error `code` — kinds never replace READ/WRITE_DENIED / HARD_OFF / RBAC.
   */
  entitlementDenials?: ReadonlyArray<EntitlementDenialItem>;
  [key: string]: unknown;
}

/**
 * Plan v1.3.1 §7 — canonical error envelope. Backend returns
 * `{ success: false, error, code?, statusCode? }` for all admin and
 * dev explore endpoints. We surface `code` so UI banners can react
 * (cap-exceeded, rate-limited, etc.) without relying on string parsing.
 */
export interface ApiErrorEnvelope {
  success: false;
  error?: string;
  message?: string;
  code?: string;
  statusCode?: number;
  /** Additive join key — same string as X-Request-ID / log correlationId. */
  requestId?: string;
  errors?: { field: string; message: string }[];
  /**
   * Structured diagnostics (ONE SoT for CAPABILITY_NOT_ENTITLED missing packs).
   * Prefer `err.response.details` — do not invent `ApiError.details`.
   * Package F: may include `entitlementDenials: { blockKey, kind }[]`.
   */
  details?: ApiErrorDetails;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  return value as Record<string, unknown>;
}

/**
 * Classifier — parse wire `details.entitlementDenials` into typed
 * `{ blockKey, kind }` rows. Drops malformed rows / unrecognized kinds.
 * Does not invent codes or change access decisions.
 */
export function parseEntitlementDenials(
  details: unknown,
): readonly EntitlementDenialItem[] {
  const record = asRecord(details);
  if (record === null) {
    return [];
  }
  const raw = record.entitlementDenials;
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: EntitlementDenialItem[] = [];
  for (const item of raw) {
    const row = asRecord(item);
    if (row === null) {
      continue;
    }
    const blockKey = row.blockKey;
    const kind = row.kind;
    if (typeof blockKey !== 'string' || blockKey.trim().length === 0) {
      continue;
    }
    if (!isClientAccessDenyKind(kind)) {
      continue;
    }
    out.push({ blockKey, kind });
  }
  return out;
}

/**
 * Read typed entitlement denials from an {@link ApiError} envelope (`response.details`).
 */
export function parseEntitlementDenialsFromApiError(
  error: unknown,
): readonly EntitlementDenialItem[] {
  if (!(error instanceof ApiError)) {
    return [];
  }
  const response = asRecord(error.response);
  if (response === null) {
    return [];
  }
  return parseEntitlementDenials(response.details);
}

/** Deny kind strings from a parsed ApiError (order preserved, no dedupe). */
export function parseEntitlementDenialKindsFromApiError(
  error: unknown,
): readonly ClientAccessDenyKind[] {
  return parseEntitlementDenialsFromApiError(error).map((item) => item.kind);
}

export class ApiError extends Error {
  public readonly code: string | undefined;
  /** Alias of `statusCode` for SWR / shared helpers that inspect `error.status`. */
  public readonly status: number;
  /** Parsed from `Retry-After` / `RateLimit-Reset` when the server sent one. */
  public readonly retryAfterMs: number | undefined;
  /** Per-response join id (= X-Request-ID / error JSON requestId). */
  public readonly correlationId: string | undefined;

  constructor(
    public readonly statusCode: number,
    public readonly response: unknown,
    message?: string,
    code?: string,
    retryAfterMs?: number,
    correlationId?: string
  ) {
    // Empty default — UI funnels sanitize via toAdminFacing / publicMessageForHttpStatus.
    // Never put "API request failed with status N" on Error.message for users.
    super(message ?? '');
    this.name = 'ApiError';
    this.code = code;
    this.status = statusCode;
    this.retryAfterMs = retryAfterMs;
    this.correlationId = correlationId;
  }

  /**
   * Package F — typed optional envelope details (entitlementDenials parsers may
   * land in parallel). Prefer this over re-parsing `response` ad hoc.
   */
  get details(): ApiErrorEnvelope['details'] | undefined {
    if (typeof this.response !== 'object' || this.response === null) {
      return undefined;
    }
    const details = (this.response as ApiErrorEnvelope).details;
    if (typeof details !== 'object' || details === null) {
      return undefined;
    }
    return details;
  }
}

export function messageFromApiErrorEnvelope(
  errorData: ApiErrorEnvelope,
  statusCode: number
): string {
  let raw: string | undefined;
  if (errorData.errors && errorData.errors.length > 0) {
    const messages = errorData.errors
      .map((entry) => {
        if (!entry.message || entry.message.trim().length === 0) {
          return undefined;
        }
        if (entry.message === 'Invalid value' && entry.field) {
          return `${entry.field}: Invalid value`;
        }
        if (entry.message !== 'Invalid value') {
          return entry.message;
        }
        return undefined;
      })
      .filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
    if (messages.length > 0) {
      raw = messages.join(' ');
    } else {
      const fields = errorData.errors.map((entry) => entry.field).filter(Boolean);
      if (fields.length > 0) {
        raw = fields.join(' ');
      } else {
        raw = errorData.message ?? errorData.error;
      }
    }
  } else {
    raw = errorData.message ?? errorData.error;
  }

  if (raw !== undefined && raw.trim().length > 0) {
    const localized = pickLocalizedApiMessage(raw, i18n.language);
    if (isUnsafeAdminApiMessage(localized) === false) {
      return localized;
    }
  }

  return publicMessageForHttpStatus(statusCode);
}

/** Controls how `Authorization` is set for a request (invite flows must bypass stored session token). */
export type RequestAuthMode =
  | { readonly kind: 'default' }
  | { readonly kind: 'none' }
  | { readonly kind: 'bearer'; readonly token: string };

export type ExecutiveAggregateStatus = 'ready' | 'lagging' | 'blocked';

export interface WatermarkDayRow {
  metricDate: string;
  status: 'SUCCESS' | 'FAILED' | 'missing';
  funnelRowCount: number;
  commerceRowCount: number;
  snapshotRowCount: number;
}

/** Response shape for `getAggregateObservability`. */
export interface AggregateObservabilityResponse {
  worker: {
    intervalMs: number;
    startedAt: string | null;
    lastRunStartedAt: string | null;
    lastRunFinishedAt: string | null;
    lastRunDurationMs: number | null;
    lastRunTenantCount: number | null;
    lastRollupFunnelRows: number | null;
    lastRollupCommerceRows: number | null;
    lastRollupSnapshotRows: number | null;
    lastRunErrorCount: number | null;
    totalRunCount: number;
    totalErrorCount: number;
    isRunning: boolean;
    nextRunAt: string | null;
  };
  engine: {
    rollupInitialized: boolean;
    lastMetricDate: string | null;
    funnelRowsWritten: number | null;
    commerceRowsWritten: number | null;
    snapshotRowsWritten: number | null;
    retentionGateNote: string;
    reconciliationNote: string;
  };
  safeThroughDate: string | null;
  lagDays: number;
  gapDates: string[];
  watermarkSummary: { success: number; failed: number; missing: number };
  watermarkDays: WatermarkDayRow[];
  retentionDeleteAllowed: boolean;
  retentionBlockReasons: string[];
  reconciliationSeverity: string;
  reconciliationCheckedAt: string | null;
  leaseHolder: string | null;
  leaseExpiresAt: string | null;
  executiveStatus: ExecutiveAggregateStatus;
  executiveStatusSentence: string;
  coldTableRowCounts?: { funnel: number; commerce: number; snapshots: number };
}

export interface AggregateRecordedMaterialization {
  recordedAt: string;
  /**
   * Monotonic process-local sequence number assigned by the backend at
   * record time. Stable, collision-free table key (resets on backend
   * restart, like the buffer itself).
   */
  seq: number;
  tenantId: number;
  factCount: number;
  bucketCount: number;
  durationMs: number;
  driftDetected: boolean;
  windowStart: string;
  windowEnd: string;
}

export interface AggregateRecordedDriftAlert {
  recordedAt: string;
  /** See `AggregateRecordedMaterialization.seq`. */
  seq: number;
  tenantId: number;
  projectionTotalAmount: number;
  factsTotalAmount: number;
  driftAmount: number;
  driftRatio: number;
  projectionCount: number;
  factsCount: number;
  windowStart: string;
  windowEnd: string;
  runbookHint: string;
}
